import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import { getCCOsByOrgIdAndFlag } from "../../redux/thunks/ccoAdvanceThunk";
import { toggleModal } from "../../redux/slices/modalSlice";
import { setConnection } from "../../redux/slices/certConnectSlice";
import { Box, Typography, useMediaQuery, useTheme, Button, TextField, InputAdornment,} from "@mui/material";
import moment from "moment";
import iconImage from "../../assets/images/user-image.png";
import "../../sass/base/ccoAdvance.scss";
import CustomColumnMenu from "../../components/MyCCOPro/CustomColumnMenu";
import { t } from "i18next";
import { DataGrid, GridColDef, GridSortModel, GridOverlay, getGridSingleSelectOperators, GridColumnVisibilityModel, } from "@mui/x-data-grid";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { FaSearch } from "react-icons/fa";
import HeaderWithFilterIcon from "../../components/MyCCOPro/HeaderWithFilterIcon";
import React from "react";
import CertConnectCustomFilterModal, { FilterItem } from "../../components/Modals/CertConnectCustomFilterModal";
import ListViewCertifiedConnection from "./ListViewCertifiedConnection";

// --- Helpers ---

const CustomNoRowsOverlay = () => (
  <GridOverlay>
    <div style={{ padding: 16, color: "#555" }}>{t("dataGrid.NoRowsLabel")}</div>
  </GridOverlay>
);
interface Props {
  activeTab: number;
}

const DataGridCertifiedConnection: React.FC<Props> = ({ activeTab = 1 }) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'LastName', sort: 'asc' }]);
  const [customFilters, setCustomFilters] = useState<FilterItem[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [clearFilter, setClearFilter] = useState(false);

  const [filters, setFilters] = useState({
    firstName: "",
    lastName: "",
    ccoId: "",
    notes: "",
    availability: null as boolean | null,
    designations: [] as string[],
    designationFilterMode: "any" as "any" | "all",
    certExpirationDate: null as string | null,
    certExpirationDateMode: "" as "on" | "onorbefore" | "onorafter" | "",
  });

  const hasHiddenColumns = useMemo(() => {
    return Object.values(columnVisibilityModel).some(value => value === false);
  }, [columnVisibilityModel]);


  const hasActiveFiltersOrSort = useMemo(() => {
    const hasFilters = customFilters.length > 0;
    const currentSort = sortModel[0] || { field: 'LastName', sort: 'asc' };
    const isDefaultSort = currentSort.field === 'LastName' && currentSort.sort === 'asc';
    const hasSorting = !isDefaultSort;
    const hasSearch = searchTerm.trim() !== "";

    return hasFilters || hasSorting || hasSearch;
  }, [customFilters, sortModel, searchTerm]);

  const handleResetFiltersAndSort = useCallback(() => {
    setColumnVisibilityModel({});
    setCustomFilters([]);
    setSearchTerm("");
    setFilters({
      firstName: "",
      lastName: "",
      ccoId: "",
      notes: "",
      availability: null,
      designations: [],
      designationFilterMode: "any",
      certExpirationDate: null,
      certExpirationDateMode: "",
    });
    setSortModel([{ field: 'LastName', sort: 'asc' }]);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setClearFilter(prev => !prev);
  }, []);

  const dispatch = useDispatch<AppDispatch>();
  const { organizationId } = useParams<{ organizationId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { ccoList, Loading, totalCertifiedCount } = useSelector((state: RootState) => state.ccoAdvance);
  const { activeDesignations } = useSelector((state: RootState) => state.designation);

  const rows = ccoList;
  const totalCount = totalCertifiedCount;
  const loading = Loading;

  const gridRows = useMemo(() => {
    return rows.map((r, i) => ({
      ...r,
      uniqueId: `${r.ContactId || 'unknown'}-${i}`
    }));
  }, [rows]);

  useEffect(() => {
    if (clearFilter) {
      setCustomFilters([]);
      setSearchTerm("");
      setFilters({
        firstName: "",
        lastName: "",
        ccoId: "",
        notes: "",
        availability: null,
        designations: [],
        designationFilterMode: "any",
        certExpirationDate: null,
        certExpirationDateMode: "",
      });
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    }
  }, [clearFilter]);

  useEffect(() => {
    setSearchTerm("");
    setCustomFilters([]);
    setFilters({
      firstName: "",
      lastName: "",
      ccoId: "",
      notes: "",
      availability: null,
      designations: [],
      designationFilterMode: "any",
      certExpirationDate: null,
      certExpirationDateMode: "",
    });
    setPaginationModel({ page: 0, pageSize: 25 });
    setSortModel([{ field: 'LastName', sort: 'asc' }]);
  }, [activeTab]);

  useEffect(() => {
    if (!organizationId) return;

    const timer = setTimeout(() => {
      const currentSort = sortModel[0] || { field: 'LastName', sort: 'asc' };

      const apiParams = {
        organizationId,
        PageNumber: paginationModel.page + 1,
        PageSize: paginationModel.pageSize,
        sortBy: currentSort.field,
        sortOrder: (currentSort.sort === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc',
        searchTerm: searchTerm.trim() || undefined,
        FirstNameSearch: filters.firstName || "",
        LastNameSearch: filters.lastName || "",
        CcoIdSearchTerm: filters.ccoId || "",
        notesSearchTerm: filters.notes || "",
        availability: filters.availability ?? undefined,
        designations: filters.designations.length > 0 ? filters.designations.join(",") : undefined,
        filterMode: filters.designationFilterMode === "all" ? 1 : 0,
        certExpirationDate: filters.certExpirationDate ? new Date(filters.certExpirationDate) : undefined,
        certExpirationDateMode: filters.certExpirationDateMode || undefined,
      };


      dispatch(getCCOsByOrgIdAndFlag(apiParams));
    }, 300);

    return () => clearTimeout(timer);
  }, [
    dispatch,
    organizationId,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    filters,
  ]);


  const handleSearch = useCallback(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    if (!organizationId) return;

    const currentSort = sortModel[0] || { field: 'LastName', sort: 'asc' };

    const apiParams = {
      organizationId,
      PageNumber: 1,
      PageSize: paginationModel.pageSize,
      sortBy: currentSort.field,
      sortOrder: (currentSort.sort === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc',
      searchTerm: searchTerm.trim() || undefined,
      FirstNameSearch: filters.firstName || "",
      LastNameSearch: filters.lastName || "",
      CcoIdSearchTerm: filters.ccoId || "",
      notesSearchTerm: filters.notes || "",
      availability: filters.availability ?? undefined,
      designations: filters.designations.length > 0 ? filters.designations.join(",") : undefined,
      filterMode: filters.designationFilterMode === "all" ? 1 : 0,
      certExpirationDate: filters.certExpirationDate ? new Date(filters.certExpirationDate) : undefined,
      certExpirationDateMode: filters.certExpirationDateMode || undefined,
    };

    dispatch(getCCOsByOrgIdAndFlag(apiParams));
  }, [dispatch, organizationId, paginationModel.pageSize, sortModel, searchTerm, filters]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model.length > 0 ? model : [{ field: 'LastName', sort: 'asc' }]);
      setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page on sort
    },
    []
  );

  const handleFilterChange = useCallback(
    (filterItems: FilterItem[]) => {
      if (JSON.stringify(filterItems) === JSON.stringify(customFilters)) return;

      setCustomFilters(filterItems);

      const newFilters = {
        firstName: "",
        lastName: "",
        ccoId: "",
        notes: "",
        availability: null as boolean | null,
        designations: [] as string[],
        designationFilterMode: "any" as "any" | "all",
        certExpirationDate: null as string | null,
        certExpirationDateMode: "" as "on" | "onorbefore" | "onorafter" | "",
      };

      filterItems.forEach((filter) => {
        if (filter.field === "Available" && filter.value === "null") {
          newFilters.availability = null;
          return;
        }

        if (!filter.value && filter.value !== false && filter.value !== 0) return;

        switch (filter.field) {
          case "FirstName":
            newFilters.firstName = String(filter.value);
            break;
          case "LastName":
            newFilters.lastName = String(filter.value);
            break;
          case "ContactCCOID":
            newFilters.ccoId = String(filter.value);
            break;
          case "Note":
            newFilters.notes = String(filter.value);
            break;
          case "Available":
            newFilters.availability =
              filter.value === "true" ? true : filter.value === "false" ? false : null;
            break;
          case "MyCCODesignationDetails":
            newFilters.designations = Array.isArray(filter.value) ? filter.value : [filter.value];
            newFilters.designationFilterMode = filter.operator === "isAnyOf" ? "any" : "all";
            break;
          case "CertExpirationDate":
            newFilters.certExpirationDate = String(filter.value);
            if (filter.operator === "on") newFilters.certExpirationDateMode = "on";
            else if (filter.operator === "onOrBefore") newFilters.certExpirationDateMode = "onorbefore";
            else if (filter.operator === "onOrAfter") newFilters.certExpirationDateMode = "onorafter";
            break;
        }
      });
      setFilters(newFilters);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    },
    [customFilters]
  );



  const columns: GridColDef[] = useMemo(() => {
    const common: GridColDef[] = [
      {
        field: "FirstName",
        headerName: t("dataGrid.FirstName"),
        headerClassName: "custom-header",
        width: 180,
        filterable: true,
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.FirstName")} />
        ),

        renderCell: (params) => (
          <div className="profile-cell">
            <img
              src={params.row.ContactProfilePic || iconImage}
              alt="profile"
            />
            <span>{params.value} </span>
          </div>
        ),
      },

      {
        field: "MiddleName",
        headerClassName: "custom-header",
        headerName: t("dataGrid.MI"),
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <span className="render-cell">{params.value || "----"}</span>
        ),
      },
      {
        field: "LastName",
        headerName: t("dataGrid.LastName"),
        headerClassName: "custom-header",
        width: 170,
        filterable: true,
        renderCell: (params) => (
          <span className="render-cell">{params.value}</span>
        ),
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.LastName")} />
        ),
      },
      {
        field: "ContactCCOID",
        headerClassName: "custom-header",
        headerName: t("dataGrid.CCOID"),
        width: 140,
        filterable: true,
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.CCOID")} />
        ),
      },
    ];


    return [
      ...common,
      {
        field: "MyCCODesignationDetails",
        headerName: t("dataGrid.Designation"),
        headerClassName: "custom-header",
        width: 190,
        type: 'singleSelect',
        valueOptions: activeDesignations?.map(d => ({ value: d.Code, label: d.Name })) || [],
        filterOperators: (() => {
          const operators = getGridSingleSelectOperators();
          const isAnyOf = operators.find(op => op.value === 'isAnyOf');
          if (!isAnyOf) return [];
          const isMulti = { ...isAnyOf, value: 'is', label: 'is' };
          return [isMulti, isAnyOf];
        })(),
        renderCell: (params) => (
          <span className="render-myCCoDes"> {params.value} </span>
        ),

        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.Designation")} />
        ),
      },
      {
        field: "Available",
        headerName: t("dataGrid.Availability"),
        headerClassName: "custom-header",
        width: 120,
        type: 'boolean',
        renderCell: (params) => {
          const isAvailable = Boolean(params.value);
          return (
            <span  className={`render-availability ${isAvailable ? "render-availability--yes" : "render-availability--no"}`}>
              {isAvailable ? t("dataGrid.Available") : t("dataGrid.Not")}
            </span>
          );
        },
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.Availability")} />
        ),
      },
      {
        field: "CertExpirationDate",
        headerName: t("dataGrid.ExpDate"),
        headerClassName: "custom-header",
        width: 180,
        type: 'date',
        valueGetter: (params) => {
          if (!params) return null;
          return new Date(params);
        },
        renderCell: (params) => {
          if (!params.value) return "-";
          const expiryDate = moment(params.value).startOf('day');
          const today = moment().startOf('day');
          const daysLeft = expiryDate.diff(today, 'days');
          const isCritical = daysLeft >= 0 && daysLeft <= 30;
          const isWarning = daysLeft > 30 && daysLeft <= 365;

          return (
          <Box component="span" className={`return-expiry-date ${
          isCritical ? "return-expiry-date--critical" :
          isWarning ? "return-expiry-date--warning" : ""
          }`}>
              {moment(expiryDate).format('YYYY-MM-DD')}
            </Box>
          );
        },
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.ExpDate")} />
        ),
      },
      {
        field: "Note",
        headerName: t("dataGrid.Note"),
        headerClassName: "custom-header",
        flex: 1,
        minwidth: 140,
        filterable: true,
        renderCell: (params) => <span>{params.value || "----"}</span>,
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.Note")} />
        ),

      }
    ];

  }, [activeDesignations, customFilters]);

  const handleMobileListRecordClick = (row: any) => {
    dispatch(
      setConnection({
        ...row,
        ContactProfilePic: row.ContactProfilePic || "",
        ContactCCOID: row.ContactCCOID || "",
        ContactName: row.FirstName + " " + row.LastName,
      })
    );
    dispatch(toggleModal("connectionModal"));
  };

  if (isMobile) {
    return (
      <ListViewCertifiedConnection
        gridRows={gridRows}
        handleSearch={handleSearch}
        totalCount={totalCount}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onSearch={handleSearch}
        onRowClick={handleMobileListRecordClick}
      />
    );
  }

  return (
    <Box className="main-box">
     <Box className="main-box__header">
      <Box className="header-content">
          <CertConnectCustomFilterModal
            filters={customFilters}
            onFiltersChange={handleFilterChange}
            isTab1={true}
            activeDesignations={activeDesignations}
            clearFilter={clearFilter}
          />

          <Typography variant="body2" color="textSecondary">
            {customFilters.length > 0 ? `${t("dataGrid.FilterApplied", { count: customFilters.length })}` : t("dataGrid.NoFilters")}
          </Typography>
          <Typography className="typography">
            {t("dataGrid.ResultsCount", { count: totalCount })}
          </Typography>
          <Box>

          </Box>
        </Box>

        <Box className="header-inner-content">
          {(hasActiveFiltersOrSort || hasHiddenColumns) && (
            <Button
              startIcon={<RestartAltIcon />}
              onClick={handleResetFiltersAndSort}
              size="small"
              variant="outlined"
              className="btn-reset">
              {t("dataGrid.Reset")}
            </Button>
          )}

          <TextField
            size="small"
            placeholder={t("dataGrid.SearchCertified")}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
           className="text-field"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FaSearch
                    className="search-icon clickable"
                    onClick={handleSearch}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
      <div className="data-grid-wrapper">
        <DataGrid
          rows={gridRows}
          columns={columns}
          rowCount={totalCount}
          loading={loading}
          getRowId={(row) => row.uniqueId}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          autoHeight
          disableRowSelectionOnClick
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            columnMenu: CustomColumnMenu,
          }}
          onRowClick={(params) => {
            dispatch(
              setConnection({
                ...params.row,
                ContactProfilePic: params.row.ContactProfilePic || "",
                ContactCCOID: params.row.ContactCCOID || "",
                ContactName:
                  params.row.ContactName || `${params.row.FirstName} ${params.row.LastName}`,
              })
            );
            dispatch(toggleModal("connectionModal"));
          }}
          localeText={{ paginationRowsPerPage: t("dataGrid.RowsPerPage") }}
        />
      </div>
    </Box>
  );
};

export default DataGridCertifiedConnection;
